'use client';

/**
 * "Enable game" — a game's tutorial video, downloaded ONCE into the browser.
 *
 * Owner, 2026-10-11: a new visitor's shelf shows video games grey with an
 * "Enable game" button; pressing it downloads the tutorial (with progress, so
 * it reads as preparing the game) into the browser's Cache Storage. From then
 * on the tile and the purchase dialog play the browser's own copy — no network,
 * no stall, on every later visit too.
 *
 * Nothing is fetched until the person asks, so hundreds of games cost nothing
 * up front. The cache key carries the video's version (its length + chapters),
 * so a re-recorded video is a new download and the old copy is dropped.
 *
 * Without Cache Storage (an insecure origin, some private windows) the copy
 * lives in memory for this page only — enabling still works, it just does not
 * survive a reload.
 */

import { useEffect, useSyncExternalStore } from 'react';
import { GAME_VIDEOS } from './gameVideos';

const CACHE = 'gt-game-videos-v1';

export type EnableState =
  | { s: 'unknown' }
  | { s: 'off' }
  | { s: 'loading'; loaded: number; total: number }
  | { s: 'on'; url: string | null }
  | { s: 'error'; message: string };

const state = new Map<string, EnableState>();
const listeners = new Set<() => void>();
const UNKNOWN: EnableState = { s: 'unknown' };
const OFF: EnableState = { s: 'off' };

function set(id: string, v: EnableState) { state.set(id, v); listeners.forEach((l) => l()); }
function subscribe(l: () => void) { listeners.add(l); return () => { listeners.delete(l); }; }

/** The file this browser should store: H.264 MP4 when it can play it (everywhere), else the WebM. */
function fileFor(id: string): { url: string; type: string } {
  const meta = GAME_VIDEOS[id];
  const v = meta ? `${meta.duration}-${meta.chapters.length}` : '0';
  let mp4 = true;
  try { mp4 = document.createElement('video').canPlayType('video/mp4; codecs="avc1.42E01E"') !== ''; } catch { /* assume yes */ }
  return mp4
    ? { url: `/games/videos/${id}.mp4?v=${v}`, type: 'video/mp4' }
    : { url: `/games/videos/${id}.webm?v=${v}`, type: 'video/webm' };
}

const hasCaches = () => typeof window !== 'undefined' && 'caches' in window && window.isSecureContext;
const memory = new Map<string, Blob>();
const checking = new Set<string>();

/** Is this game's video already in the browser? (Cheap: no body is read.) */
async function check(id: string) {
  if (checking.has(id) || state.has(id)) return;
  checking.add(id);
  try {
    if (!hasCaches()) { set(id, OFF); return; }
    const c = await caches.open(CACHE);
    const hit = await c.match(fileFor(id).url);
    set(id, hit ? { s: 'on', url: null } : OFF);
  } catch { set(id, OFF); } finally { checking.delete(id); }
}

const opening = new Map<string, Promise<string | null>>();
/** A playable URL for the stored copy (an object URL), read from disk once per page. */
export function localVideoUrl(id: string): Promise<string | null> {
  const cur = state.get(id);
  if (cur?.s === 'on' && cur.url) return Promise.resolve(cur.url);
  let p = opening.get(id);
  if (p) return p;
  p = (async () => {
    try {
      let blob = memory.get(id) ?? null;
      if (!blob && hasCaches()) {
        const hit = await (await caches.open(CACHE)).match(fileFor(id).url);
        blob = hit ? await hit.blob() : null;
      }
      if (!blob) return null;
      const url = URL.createObjectURL(blob);
      set(id, { s: 'on', url });
      return url;
    } catch { return null; } finally { opening.delete(id); }
  })();
  opening.set(id, p);
  return p;
}

const running = new Map<string, Promise<void>>();
/** Download the tutorial into the browser, reporting progress. Safe to call twice. */
export function enableGame(id: string): Promise<void> {
  const r = running.get(id);
  if (r) return r;
  const job = (async () => {
    const { url, type } = fileFor(id);
    set(id, { s: 'loading', loaded: 0, total: 0 });
    try {
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const total = Number(res.headers.get('content-length')) || 0;
      const reader = res.body.getReader();
      const parts: BlobPart[] = [];
      let loaded = 0, last = 0;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        parts.push(value as BlobPart);
        loaded += value.byteLength;
        const now = performance.now();
        if (now - last > 80) { last = now; set(id, { s: 'loading', loaded, total }); }
      }
      set(id, { s: 'loading', loaded, total: total || loaded });
      const blob = new Blob(parts, { type });
      memory.set(id, blob);
      if (hasCaches()) {
        try {
          const c = await caches.open(CACHE);
          // Drop older versions of this game's video, then store this one.
          for (const k of await c.keys()) if (new URL(k.url).pathname.startsWith(`/games/videos/${id}.`)) await c.delete(k);
          await c.put(url, new Response(blob, { headers: { 'Content-Type': type, 'Content-Length': String(blob.size) } }));
          void navigator.storage?.persist?.().catch(() => undefined);
        } catch { /* quota / private mode: the in-memory copy still plays this visit */ }
      }
      set(id, { s: 'on', url: URL.createObjectURL(blob) });
      memory.delete(id);
    } catch (e) {
      set(id, { s: 'error', message: (e as Error).message || 'Download failed' });
    } finally { running.delete(id); }
  })();
  running.set(id, job);
  return job;
}

/** Is the tutorial already playable from this browser (as far as this page knows)? */
export function videoReady(id: string): boolean {
  return !GAME_VIDEOS[id] || state.get(id)?.s === 'on';
}

/**
 * Make sure the tutorial is in the browser: resolves true at once when it is
 * stored, otherwise downloads it (the tile shows the progress) and resolves
 * true once it is ready, false when the download failed.
 */
export async function ensureVideo(id: string): Promise<boolean> {
  if (videoReady(id)) return true;
  if (state.get(id)?.s !== 'loading') {
    try {
      if (hasCaches() && await (await caches.open(CACHE)).match(fileFor(id).url)) { set(id, { s: 'on', url: null }); return true; }
    } catch { /* fall through to the download */ }
  }
  await enableGame(id);
  return videoReady(id);
}

/** This game's enable state, kept live across every tile / dialog showing it. */
export function useGameEnable(id: string, active = true): EnableState {
  const has = !!GAME_VIDEOS[id];
  const v = useSyncExternalStore(subscribe, () => state.get(id) ?? UNKNOWN, () => UNKNOWN);
  useEffect(() => { if (has && active) void check(id); }, [id, has, active]);
  return has ? v : OFF;
}

export const fmtMB = (b: number) => `${(b / 1048576).toFixed(1)} MB`;
